/**
 * D-EZ-111 — WHAT THE BOOTH KNOWS ABOUT THE LEAGUE, GOING INTO ONE RACE.
 *
 * One object, built once per race on screen, from the league's history UP TO
 * THE PREVIOUS RACE — never this race, never anything after it:
 *
 *   • the cast (hashed from the league key — `castFor`);
 *   • the season context (`seasonContextBefore`: points, bubble, streaks,
 *     track history, the rivalry heat);
 *   • the people layer cut at the previous race (personalities, the rivalry
 *     book with every remembered moment);
 *   • last week: who won, and the analyst's pick and how it finished — derived
 *     again from last week's inputs, so nothing is stored.
 *
 * The shell advances the season the moment the green flag drops, so the live
 * season may already hold THIS race's result. Every read here is cut first.
 */

import type { BoothSeasonContext } from './context.ts';
import { castFor, DEFAULT_CAST, type Cast } from './cast.ts';
import { analystPick, type AnalystPick } from './analyst.ts';
import { leagueSeasons, seasonContextBefore, type LeagueShelf } from '../story/league.ts';
import { leagueKeyOf, peopleContext, type PeopleContext } from '../people/context.ts';
import { liveHistorySeason, sortedSeasons, type HistorySeason } from '../history/source.ts';
import { SEASON_RACES } from '../schedule.ts';

export interface BoothRaceRef {
  raceNumber: number;
  raceName: string;
  trackName: string;
  venue: string;
  category: string;
}

export interface BoothStandingRow {
  driverId: string;
  name: string;
  rank: number;
  points: number;
  wins: number;
}

export interface BoothLastRace extends BoothRaceRef {
  winnerId: string | null;
  winnerName: string | null;
  /** Finishing order, place first. */
  order: { driverId: string; name: string; place: number; out: boolean }[];
}

export interface BoothLastPick {
  driverId: string;
  name: string;
  place: number | null;
  out: boolean;
  won: boolean;
  raceName: string;
  trackName: string;
}

export interface BoothLeague {
  key: string;
  cast: Cast;
  seasonNumber: number;
  raceNumber: number;
  /** Races in a season, for "race 5 of 36". */
  seasonRaces: number;
  /** This race, as scheduled. */
  race: BoothRaceRef | null;
  /** The next race on the calendar, for the sign-off. Null at the finale. */
  next: { raceName: string; trackName: string } | null;
  season: BoothSeasonContext | null;
  /** The people layer, cut at the previous race. */
  people: PeopleContext | null;
  /** The table going in, leader first. Empty before race 1. */
  table: BoothStandingRow[];
  lastRace: BoothLastRace | null;
  /** The analyst's pick last week and how it finished. */
  lastPick: BoothLastPick | null;
}

function orderOf(startPositions: Record<string, number>): string[] {
  return Object.entries(startPositions)
    .sort((a, b) => a[1] - b[1])
    .map(([id]) => id);
}

/** The first roster of the league, for the cast's name-collision check. */
function firstRosterNames(seasons: readonly HistorySeason[]): string[] {
  const first = sortedSeasons([...seasons])[0];
  return first ? first.roster.map((c) => c.name) : [];
}

/**
 * The booth's league for race `raceNumber` of the live season. Null outside a
 * season (an exhibition gets `exhibitionLeague`).
 *
 * `season` may be handed in when the caller already built it (App does).
 */
export function boothLeagueFor(
  shelf: LeagueShelf,
  raceNumber: number,
  season?: BoothSeasonContext | null,
): BoothLeague | null {
  const live = shelf.live;
  if (!live) return null;
  try {
    const all = leagueSeasons(shelf);
    // The key of the league's FIRST season — the same before and after race 1
    // is recorded, so the booth never changes cast mid-league.
    const key = leagueKeyOf(all.length ? all : [liveHistorySeason(live)]);
    const cast = castFor(key, firstRosterNames(all));
    const ctx = season === undefined ? seasonContextBefore(shelf, raceNumber) : season;
    const people = peopleContext({ archive: all, leagueKey: key, throughRace: { seasonNumber: live.seasonNumber, raceNumber: raceNumber - 1 } });
    const sched = live.state.schedule.races;
    const here = sched[raceNumber - 1];
    const after = sched[raceNumber];
    const ref = (r: (typeof sched)[number]): BoothRaceRef => ({
      raceNumber: r.raceNumber,
      raceName: r.raceName,
      trackName: r.trackName,
      venue: r.venue ?? r.trackId,
      category: r.category,
    });

    // The table going in: standings after the previous race.
    const before = live.state.standingsAfterRace[raceNumber - 2];
    const table: BoothStandingRow[] = (before?.drivers ?? []).map((d) => ({ driverId: d.driverId, name: d.name, rank: d.rank, points: d.points, wins: d.wins }));

    // Last week, from its recorded result.
    const prev = live.state.races.find((r) => r.raceNumber === raceNumber - 1) ?? null;
    let lastRace: BoothLastRace | null = null;
    let lastPick: BoothLastPick | null = null;
    if (prev) {
      const order = prev.race.finishingOrder.map((f) => ({ driverId: f.driverId, name: f.name, place: f.place, out: f.status === 'out' }));
      lastRace = {
        ...ref(prev.scheduled),
        winnerId: prev.race.winnerId,
        winnerName: order.find((o) => o.driverId === prev.race.winnerId)?.name ?? null,
        order,
      };
      const prevCtx = seasonContextBefore(shelf, raceNumber - 1);
      const nameOf = (id: string) => order.find((o) => o.driverId === id)?.name ?? id;
      const pick: AnalystPick | null = analystPick(cast, prevCtx, orderOf(prev.startPositions), nameOf, `${key}|${live.seasonNumber}|${raceNumber - 1}`);
      if (pick) {
        const fin = order.find((o) => o.driverId === pick.driverId);
        lastPick = {
          driverId: pick.driverId,
          name: pick.name,
          place: fin?.place ?? null,
          out: fin?.out ?? false,
          won: prev.race.winnerId === pick.driverId,
          raceName: prev.scheduled.raceName,
          trackName: prev.scheduled.trackName,
        };
      }
    }

    return {
      key,
      cast,
      seasonNumber: live.seasonNumber,
      raceNumber,
      seasonRaces: sched.length || SEASON_RACES,
      race: here ? ref(here) : null,
      next: after ? { raceName: after.raceName, trackName: after.trackName } : null,
      season: ctx ?? null,
      people,
      table,
      lastRace,
      lastPick,
    };
  } catch {
    return null;
  }
}

/** An exhibition: the default booth, no league memory. */
export function exhibitionLeague(): BoothLeague {
  return {
    key: 'exhibition',
    cast: DEFAULT_CAST,
    seasonNumber: 0,
    raceNumber: 0,
    seasonRaces: 0,
    race: null,
    next: null,
    season: null,
    people: null,
    table: [],
    lastRace: null,
    lastPick: null,
  };
}

/** The league's race index — moves by one every race, for line rotation. */
export function leagueSeq(league: BoothLeague | null | undefined, fallback: number): number {
  if (!league || league.seasonNumber === 0) return fallback;
  return (league.seasonNumber - 1) * Math.max(1, league.seasonRaces) + league.raceNumber;
}
